import { useIdpAuth } from '@/context/IdpAuth/IdpAuthContext';
import { UniversesOptions, useMenuLateral } from '@/context/MenuLateral';
import { useUser } from '@/context/UserContext';
import { useViewMode } from '@/context/ViewMode/ViewMode';

import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { FeatureFlagsOptions } from '@/hooks/useFeatureFlags/types';
import { useRequestSupportModal } from '@/hooks/useRequestModal';

import { ActivitiesProps } from '@/pages/Home/context/FavoriteActivitiesContext';

import LogoUniverseFinances from '@/components/Sidebar/icons/Universes/LogoUniverseFinances';
import LogoUniverseHost from '@/components/Sidebar/icons/Universes/LogoUniverseHost';
import LogoUniverseOperational from '@/components/Sidebar/icons/Universes/LogoUniverseOperational';
import LogoUniverseOwner from '@/components/Sidebar/icons/Universes/LogoUniverseOwner';
import LogoUniversePartner from '@/components/Sidebar/icons/Universes/LogoUniversePartner';
import NavItemComponent from '@/components/Sidebar/NavItemComponent';

import { GUESTWATCH_URL } from '@/constants/urls';

import ExpansionRequestsIndicator from '../ExpansionRequestsIndicator';
import LogoSapronLabs from '../icons/Universes/LogoSapronLabs';

import { abrirEmNovaAba, tokenParaLink } from '@/lib/idp/link-com-token';

// Quem vê "Mapa" e "Pedidos de ampliação": a mesma lista da rota `/geo`.
const SAPRON_LABS_URL = 'https://sapron.retool.com/p/labs/menu_universe';

const GEO_MENU_PERMISSIONS = [
  'Admin',
  'Seazone',
  'SeazoneOnboarding',
  'SeazoneAdministrative',
  'SeazoneMarketing',
  'SeazoneOTA',
  'SeazonePricing',
  'SeazoneSysAdmin',
];

const useUniverseMappings = () => {
  const { categoryActive } = useMenuLateral();
  const { userInformation, user } = useUser();
  const { setIsOpenedRequestHostOpsModal } = useRequestSupportModal();
  const { isViewMode } = useViewMode();
  const { featureFlags } = useFeatureFlags();
  const { renovarTokenParaLink } = useIdpAuth();
  // Em local força exibir o item pra facilitar testes do piloto.
  const centralAtividadesEnabled =
    (typeof window !== 'undefined' &&
      ['localhost', '127.0.0.1'].includes(window.location.hostname)) ||
    featureFlags[FeatureFlagsOptions.FF_CENTRAL_ATIVIDADES_SPN];
  // Sem override de localhost: a flag controla de verdade (off => some em todo lugar).
  const manutencaoEnabled = featureFlags[FeatureFlagsOptions.FF_MANUTENCAO];
  // Channel Manager e um item agrupado dentro de "Operacional", nao um
  // universo proprio na sidebar.
  const channelManagerEnabled = featureFlags[FeatureFlagsOptions.FF_CHANNEL_MANAGER_ENABLED];

  type UniversesNavItemComponentOptions = Record<UniversesOptions, JSX.Element>;

  const handleNavigationToScore = () => {
    window.open(`https://scorepulse.seazone.com.br`, '_blank');
  };

  const handleNavigationToStore = () => {
    window.open('https://lojaitens.seazone.com.br', '_blank', 'noopener noreferrer');
  };

  const handleNavigationToGuestWatch = () => {
    window.open(GUESTWATCH_URL, '_blank', 'noopener noreferrer');
  };

  // Token na URL: excecao do ADR 005 do seazone-idp (so Retool e GeoSeazone).
  // Sem perspectiva: o Retool age com a identidade de quem esta logado.
  const handleNavigationToSapronLabs = () =>
    abrirEmNovaAba(SAPRON_LABS_URL, async () => {
      const token = await tokenParaLink({
        legado: user?.access,
        renovar: renovarTokenParaLink,
      });
      return token ? `${SAPRON_LABS_URL}?token=${encodeURIComponent(token)}` : SAPRON_LABS_URL;
    });

  const universeMappingsBackoffice: UniversesNavItemComponentOptions = {
    Operacional: (
      <>
        <NavItemComponent
          title="Multicalendar"
          path="/multicalendar"
          permissions={['Admin', 'SeazoneAdministrative', 'Attendant']}
        />
        <NavItemComponent
          title="Mapa"
          path="/geo"
          permissions={[
            'Admin',
            'Seazone',
            'SeazoneOnboarding',
            'SeazoneAdministrative',
            'SeazoneMarketing',
            'SeazoneOTA',
            'SeazonePricing',
            'SeazoneSysAdmin',
          ]}
        />
        {/* Fila de pedidos de ampliação de área de atuação: item irmão de
            "Mapa" (o Sidebar não tem submenu), mesmas permissões. A bolinha
            acende quando há pedido em análise. */}
        <NavItemComponent
          title="Pedidos de ampliação"
          path="/geo/expansion-requests"
          permissions={GEO_MENU_PERMISSIONS}
          indicator={<ExpansionRequestsIndicator permissions={GEO_MENU_PERMISSIONS} />}
        />
        <NavItemComponent title="Hóspede" permissions={['Admin', 'Attendant']} />
        <NavItemComponent
          title="Reservas para Completar"
          path="/compreservas"
          permissions={['Admin', 'Attendant']}
          className={
            categoryActive.title === 'Hóspede' && categoryActive.actived ? 'expanded' : 'collapsed'
          }
        />
        <NavItemComponent
          title="Vouchers Enviados"
          path="/vouchersenviados"
          permissions={['Admin', 'Attendant']}
          className={
            categoryActive.title === 'Hóspede' && categoryActive.actived ? 'expanded' : 'collapsed'
          }
        />
        <NavItemComponent
          title="Lançamento de BOs"
          path="/lancamento-bo"
          permissions={['Admin', 'Attendant', 'SeazoneAdministrative', 'SeazoneOnboarding']}
          className={
            categoryActive.title === 'Hóspede' && categoryActive.actived ? 'expanded' : 'collapsed'
          }
        />
        <NavItemComponent title="Anúncio" permissions={['Admin', 'SeazoneOnboarding']} />
        <NavItemComponent
          title="Novo Listing"
          path="/inserirdados/novo-listing"
          permissions={['Admin', 'SeazoneOnboarding']}
          className={
            categoryActive.title === 'Anúncio' && categoryActive.actived ? 'expanded' : 'collapsed'
          }
        />
        <NavItemComponent
          title="Editar Listing"
          path="/editardados/listing"
          permissions={['Admin', 'SeazoneOnboarding']}
          className={
            categoryActive.title === 'Anúncio' && categoryActive.actived ? 'expanded' : 'collapsed'
          }
        />

        <NavItemComponent
          title="Status dos Anúncios"
          path="/statusdosanuncios"
          permissions={['Admin', 'SeazoneOnboarding']}
          className={
            categoryActive.title === 'Anúncio' && categoryActive.actived ? 'expanded' : 'collapsed'
          }
        />

        <NavItemComponent
          title="Implantação"
          permissions={['Admin', 'SeazoneOnboarding', 'Attendant']}
        />
        <NavItemComponent
          title="Pesquisar Propriedade"
          path="/propriedades"
          permissions={['Admin', 'SeazoneOnboarding', 'Attendant']}
          className={
            categoryActive.title === 'Implantação' && categoryActive.actived
              ? 'expanded'
              : 'collapsed'
          }
        />
        <NavItemComponent
          title="Onboarding"
          path="/onboarding"
          permissions={['Admin', 'SeazoneOnboarding']}
          className={
            categoryActive.title === 'Implantação' && categoryActive.actived
              ? 'expanded'
              : 'collapsed'
          }
        />
        <NavItemComponent
          title="Editar Propriedade"
          path="/editardados/propriedade"
          permissions={['Admin', 'SeazoneOnboarding']}
          className={
            categoryActive.title === 'Implantação' && categoryActive.actived
              ? 'expanded'
              : 'collapsed'
          }
        />
        <NavItemComponent
          title="Mudança de Status"
          path="/inserirdados/mudar-status-imovel"
          permissions={['Admin', 'SeazoneOnboarding']}
          className={
            categoryActive.title === 'Implantação' && categoryActive.actived
              ? 'expanded'
              : 'collapsed'
          }
        />
        <NavItemComponent
          title="Comodidades"
          path="/inserirdados/property-amenities"
          permissions={['Admin', 'SeazoneOnboarding']}
          className={
            categoryActive.title === 'Implantação' && categoryActive.actived
              ? 'expanded'
              : 'collapsed'
          }
        />
        <NavItemComponent
          title="Regras"
          path="/inserirdados/regras"
          permissions={['Admin', 'SeazoneOnboarding']}
          className={
            categoryActive.title === 'Implantação' && categoryActive.actived
              ? 'expanded'
              : 'collapsed'
          }
        />
        {channelManagerEnabled && (
          <NavItemComponent
            title="Channel Manager"
            path="/channelmanager"
            permissions={['Admin', 'SeazoneOTA', 'SeazoneOnboarding', 'Attendant']}
          />
        )}
      </>
    ),
    Financeiro: (
      <>
        <NavItemComponent
          title="Multicalendar"
          path="/multicalendar"
          permissions={['Admin', 'SeazoneAdministrative', 'Attendant']}
        />

        <NavItemComponent
          title="Despesas"
          path="/despesas"
          permissions={['Admin', 'SeazoneAdministrative', 'SeazoneOnboarding']}
          verifyHostOps
        />

        <NavItemComponent
          title="Danos de Hóspede"
          path="/danosdehospede"
          permissions={['Admin', 'Attendant', 'SeazoneAdministrative', 'SeazoneOnboarding']}
        />

        <NavItemComponent
          title="Lista de NF"
          path="/listadenfs"
          permissions={['Admin', 'SeazoneAdministrative']}
        />

        <NavItemComponent
          title="Lista de Repasses"
          permissions={['Admin', 'SeazoneAdministrative']}
        />
        <NavItemComponent
          title="Proprietário"
          path="/listaderepassesproprietario"
          permissions={['Admin', 'SeazoneAdministrative']}
          className={
            categoryActive.title === 'Lista de Repasses' && categoryActive.actived
              ? 'expanded'
              : 'collapsed'
          }
        />
        <NavItemComponent
          title="Anfitrião"
          path="/listaderepassesanfitriao"
          permissions={['Admin', 'SeazoneAdministrative']}
          className={
            categoryActive.title === 'Lista de Repasses' && categoryActive.actived
              ? 'expanded'
              : 'collapsed'
          }
        />

        <NavItemComponent title="Fechamento" permissions={['Admin', 'SeazoneAdministrative']} />
        <NavItemComponent
          title="Imóvel"
          path="/fechamentoimovel"
          permissions={['Admin', 'SeazoneAdministrative']}
          className={
            categoryActive.title === 'Fechamento' && categoryActive.actived
              ? 'expanded'
              : 'collapsed'
          }
        />
        <NavItemComponent
          title="Anfitrião"
          path="/fechamentoanfitriao"
          permissions={['Admin', 'SeazoneAdministrative']}
          className={
            categoryActive.title === 'Fechamento' && categoryActive.actived
              ? 'expanded'
              : 'collapsed'
          }
        />

        <NavItemComponent
          title="Painel Gerenciamento"
          path="/painelgerenciamento"
          permissions={['Admin', 'SeazoneAdministrative', 'SeazoneOnboarding', 'Attendant']}
        />

        <NavItemComponent
          title="Conciliação"
          path="/conciliacao"
          permissions={['Admin', 'SeazoneAdministrative']}
        />

        <NavItemComponent
          title="Solicitações de Resgate"
          path="/solicitacoes-resgate-parceiro"
          permissions={['Admin', 'SeazoneAdministrative']}
        />
      </>
    ),
    Proprietário: (
      <>
        <NavItemComponent
          title="Lista"
          path="/proprietarios"
          permissions={['Admin', 'SeazoneOnboarding']}
        />

        <NavItemComponent
          title="Editar"
          path="/editardados/proprietario"
          permissions={['Admin', 'SeazoneOnboarding']}
        />

        <NavItemComponent
          title="Onboarding"
          path="/onboarding"
          permissions={['Admin', 'SeazoneOnboarding']}
        />

        <NavItemComponent
          title="Perspectiva do Proprietário"
          path="/painel-perspectiva-do-proprietario"
          permissions={['Admin', 'SeazoneAdministrative', 'SeazoneOnboarding']}
        />

        <NavItemComponent
          title="Easy Cover"
          path="/easy-cover"
          permissions={['Admin', 'SeazoneAdministrative', 'SeazoneOnboarding']}
        />
      </>
    ),
    Anfitrião: (
      <>
        <NavItemComponent title="Controle" path="/controle" permissions={['Admin']} />

        <NavItemComponent
          title="Pesquisar"
          path="/anfitrioes"
          permissions={['Admin', 'SeazoneOnboarding']}
        />

        <NavItemComponent
          title="Inserir"
          path="/inserirdados/anfitriao"
          permissions={['Admin', 'SeazoneOnboarding']}
        />

        <NavItemComponent
          title="Editar"
          path="/editardados/anfitriao"
          permissions={['Admin', 'SeazoneOnboarding']}
        />

        <NavItemComponent
          title="Troca de Anfitrião do Imóvel"
          path="/editardados/mudar-anfitriao"
          permissions={['Admin', 'SeazoneOnboarding']}
        />

        <NavItemComponent
          title="Migrações"
          path="/migracoes"
          permissions={['Admin', 'SeazoneAdministrative', 'SeazoneOnboarding']}
        />

        <NavItemComponent
          title="Operações de Franquia"
          path="/gestao-operacoes-franquia"
          permissions={['Admin', 'SeazoneOnboarding']}
        />

        <NavItemComponent
          title="Danos de Hóspede"
          path="/danosdehospede"
          permissions={['Admin', 'Attendant', 'SeazoneAdministrative', 'SeazoneOnboarding']}
        />

        <NavItemComponent
          title="Perspectiva do Anfitrião"
          path="/painel-perspectiva-do-anfitriao"
          permissions={['Admin', 'Attendant', 'SeazoneAdministrative', 'SeazoneOnboarding']}
        />

        <NavItemComponent
          title="Perspectiva do Franqueado"
          path="/painel-perspectiva-do-franqueado"
          permissions={['Admin', 'SeazoneAdministrative', 'SeazoneOnboarding']}
        />

        <NavItemComponent
          title="Gestão de Tarefas"
          path="/gestaodetarefas"
          permissions={['Admin', 'Attendant', 'SeazoneAdministrative', 'SeazoneOnboarding']}
        />
        {centralAtividadesEnabled && (
          <NavItemComponent
            title="Central de Atividades"
            path="/central-atividades"
            permissions={['Admin', 'Attendant', 'SeazoneAdministrative', 'SeazoneOnboarding']}
          />
        )}
        {manutencaoEnabled && (
          <NavItemComponent
            title="Central de Manutenção"
            path="/central-manutencao"
            permissions={['Admin', 'Attendant', 'SeazoneAdministrative', 'SeazoneOnboarding']}
          />
        )}
        <NavItemComponent
          title="Central de Churn"
          path="/central-churn"
          permissions={['Admin', 'Attendant', 'SeazoneAdministrative', 'SeazoneOnboarding']}
        />
      </>
    ),
    Parceiros: (
      <>
        <NavItemComponent
          title="Aprovar Lista"
          path="/admin/aprovar-lista"
          permissions={['Admin', 'SeazoneAdministrative']}
        />
        <NavItemComponent
          title="Gerenciar Comissões"
          path="/gestao-comissoes-parceiros"
          permissions={['Admin', 'SeazoneAdministrative']}
        />
        <NavItemComponent
          title="Editar Parceiro"
          path="/editardados/parceiro"
          permissions={['Admin', 'SeazoneOnboarding']}
        />
        <NavItemComponent
          title="Gerenciar Executivos"
          path="/gerenciar-executivos"
          permissions={['Admin', 'SeazoneOnboarding']}
        />
        <NavItemComponent title="Novos Dados" permissions={['Admin', 'SeazoneOnboarding']} />
        <NavItemComponent
          title="Parceiro"
          path="/inserirdados/parceiro"
          permissions={['Admin', 'SeazoneOnboarding']}
          className={
            categoryActive.title === 'Novos Dados' && categoryActive.actived
              ? 'expanded'
              : 'collapsed'
          }
        />

        <NavItemComponent
          title="Condomínio B2B"
          path="/inserirdados/condominio-b2b"
          permissions={['Admin', 'SeazoneOnboarding']}
          className={
            categoryActive.title === 'Novos Dados' && categoryActive.actived
              ? 'expanded'
              : 'collapsed'
          }
        />

        <NavItemComponent
          title="Perspectiva do Parceiro"
          path="/painel-perspectiva-do-parceiro"
          permissions={['Admin', 'SeazoneAdministrative', 'SeazoneOnboarding']}
        />
      </>
    ),
    'Sapron Labs': (
      <NavItemComponent
        title="Acessar Sapron Labs"
        handleOnClick={handleNavigationToSapronLabs}
        permissions={['Admin', 'Attendant', 'SeazoneAdministrative', 'SeazoneOnboarding']}
      />
    ),
  };

  const universeMappingsHost: UniversesNavItemComponentOptions = {
    Operacional: (
      <>
        <NavItemComponent title="Controle" path="/controle" permissions={['Host']} />

        <NavItemComponent title="Multicalendar" path="/multicalendar" permissions={['Host']} />

        <NavItemComponent title="Danos de Hóspede" path="/danosdehospede" permissions={['Host']} />

        <NavItemComponent
          title="Pesquisar Propriedade"
          path="/propriedades"
          permissions={['Host']}
        />

        <NavItemComponent
          title="Criação de Subconta"
          permissions={['Host']}
          disableDropIcon
          verifyHostOps
          handleOnClick={() => (isViewMode ? null : setIsOpenedRequestHostOpsModal(true))}
        />

        <NavItemComponent
          title="Gestão de Tarefas"
          path="/gestaodetarefas"
          permissions={['Host']}
        />

        {featureFlags?.ff_enable_guestwatch && (
          <NavItemComponent
            title="Denunciar Hóspede"
            handleOnClick={handleNavigationToGuestWatch}
            permissions={['Host']}
            disableDropIcon
          />
        )}
      </>
    ),
    Financeiro: (
      <>
        <NavItemComponent title="Pedido de Reembolso" path="/reembolso" permissions={['Host']} />
        <NavItemComponent
          title="Dashboard"
          path="/dashboard"
          permissions={['Host']}
          verifyHostOps
        />

        <NavItemComponent
          title="Loja Seazone"
          handleOnClick={handleNavigationToStore}
          permissions={['Host']}
          disableDropIcon
        />
      </>
    ),
    Proprietário: <></>,
    Anfitrião: (
      <>
        <NavItemComponent
          title="Minhas Informações"
          path={'/minhasinformacoes'}
          permissions={['Host']}
        />

        <NavItemComponent
          title="Score da Franquia"
          handleOnClick={handleNavigationToScore}
          permissions={['Host']}
          disableDropIcon
        />
      </>
    ),
    Parceiros: (
      <>
        <NavItemComponent
          title="Programa de Indicações"
          path="/parceiros/painel"
          permissions={['Partner']}
          verifyHostOps
        />
      </>
    ),
    'Sapron Labs': <></>,
  };

  type UniversesActivitiesOptions = Record<
    UniversesOptions,
    Array<Omit<ActivitiesProps, 'universe'>>
  >;

  const activitiesMappingsBackoffice: UniversesActivitiesOptions = {
    Operacional: [
      {
        title: 'Multicalendar',
        path: '/multicalendar',
        permissions: ['Admin', 'SeazoneAdministrative', 'Attendant'],
      },
      {
        title: 'Mapa',
        path: '/geo',
        permissions: [
          'Admin',
          'Seazone',
          'SeazoneOnboarding',
          'SeazoneAdministrative',
          'SeazoneMarketing',
          'SeazoneOTA',
          'SeazonePricing',
          'SeazoneSysAdmin',
        ],
      },
      {
        title: 'Pedidos de ampliação',
        path: '/geo/expansion-requests',
        permissions: [
          'Admin',
          'Seazone',
          'SeazoneOnboarding',
          'SeazoneAdministrative',
          'SeazoneMarketing',
          'SeazoneOTA',
          'SeazonePricing',
          'SeazoneSysAdmin',
        ],
      },
      {
        title: 'Reservas para Completar',
        path: '/compreservas',
        permissions: ['Admin', 'Attendant'],
      },
      {
        title: 'Vouchers Enviados',
        path: '/vouchersenviados',
        permissions: ['Admin', 'Attendant'],
      },
      {
        title: 'Lançamento de BOs',
        path: '/lancamento-bo',
        permissions: ['Admin', 'Attendant', 'SeazoneAdministrative', 'SeazoneOnboarding'],
      },
      {
        title: 'Novo Listing',
        path: '/inserirdados/novo-listing',
        permissions: ['Admin', 'SeazoneOnboarding'],
      },
      {
        title: 'Editar Listing',
        path: '/editardados/listing',
        permissions: ['Admin', 'SeazoneOnboarding'],
      },
      {
        title: 'Pesquisar Propriedade',
        path: '/propriedades',
        permissions: ['Admin', 'SeazoneOnboarding', 'Attendant'],
      },
      {
        title: 'Onboarding',
        path: '/onboarding',
        permissions: ['Admin', 'SeazoneOnboarding'],
      },
      {
        title: 'Editar Propriedade',
        path: '/editardados/propriedade',
        permissions: ['Admin', 'SeazoneOnboarding'],
      },
      {
        title: 'Mudança de Status',
        path: '/inserirdados/mudar-status-imovel',
        permissions: ['Admin', 'SeazoneOnboarding'],
      },
      {
        title: 'Comodidades',
        path: '/inserirdados/property-amenities',
        permissions: ['Admin', 'SeazoneOnboarding'],
      },
      {
        title: 'Regras',
        path: '/inserirdados/regras',
        permissions: ['Admin', 'SeazoneOnboarding'],
      },
    ].filter(Boolean),
    Financeiro: [
      {
        title: 'Multicalendar',
        path: '/multicalendar',
        permissions: ['Admin', 'SeazoneAdministrative', 'Attendant'],
      },
      {
        title: 'Despesas',
        path: '/despesas',
        permissions: ['Admin', 'SeazoneAdministrative', 'SeazoneOnboarding'],
      },
      {
        title: 'Danos de Hóspede',
        path: '/danosdehospede',
        permissions: ['Admin', 'Attendant', 'SeazoneAdministrative', 'SeazoneOnboarding'],
      },
      {
        title: 'Lista de NF',
        path: '/listadenfs',
        permissions: ['Admin', 'SeazoneAdministrative'],
      },
      {
        title: 'Repasses > Proprietário',
        path: '/listaderepassesproprietario',
        permissions: ['Admin', 'SeazoneAdministrative'],
      },
      {
        title: 'Repasses > Anfitrião',
        path: '/listaderepassesanfitriao',
        permissions: ['Admin', 'SeazoneAdministrative'],
      },
      {
        title: 'Fechamento > Imóvel',
        path: '/fechamentoimovel',
        permissions: ['Admin', 'SeazoneAdministrative'],
      },
      {
        title: 'Fechamento > Anfitrião',
        path: '/fechamentoanfitriao',
        permissions: ['Admin', 'SeazoneAdministrative'],
      },
      {
        title: 'Painel Gerenciamento',
        path: '/painelgerenciamento',
        permissions: ['Admin', 'SeazoneAdministrative', 'SeazoneOnboarding', 'Attendant'],
      },
    ],
    Proprietário: [
      {
        title: 'Lista',
        path: '/proprietarios',
        permissions: ['Admin', 'SeazoneOnboarding'],
      },
      {
        title: 'Editar',
        path: '/editardados/proprietario',
        permissions: ['Admin', 'SeazoneOnboarding'],
      },
      {
        title: 'Onboarding',
        path: '/onboarding',
        permissions: ['Admin', 'SeazoneOnboarding'],
      },
      {
        title: 'Perspectiva do Proprietário',
        path: '/painel-perspectiva-do-proprietario',
        permissions: ['Admin', 'SeazoneAdministrative', 'SeazoneOnboarding'],
      },
    ],
    Anfitrião: [
      {
        title: 'Controle',
        path: '/controle',
        permissions: ['Admin'],
      },
      {
        title: 'Pesquisar',
        path: '/anfitrioes',
        permissions: ['Admin', 'SeazoneOnboarding'],
      },
      {
        title: 'Inserir',
        path: '/inserirdados/anfitriao',
        permissions: ['Admin', 'SeazoneOnboarding'],
      },
      {
        title: 'Editar',
        path: '/editardados/anfitriao',
        permissions: ['Admin', 'SeazoneOnboarding'],
      },
      {
        title: 'Troca de Anfitrião do Imóvel',
        path: '/editardados/mudar-anfitriao',
        permissions: ['Admin', 'SeazoneOnboarding'],
      },
      {
        title: 'Migrações',
        path: '/migracoes',
        permissions: ['Admin', 'SeazoneAdministrative', 'SeazoneOnboarding'],
      },
      {
        title: 'Danos de Hóspede',
        path: '/danosdehospede',
        permissions: ['Admin', 'Attendant', 'SeazoneAdministrative', 'SeazoneOnboarding'],
      },
      {
        title: 'Perspectiva do Anfritrião',
        path: '/painel-perspectiva-do-anfritriao',
        permissions: ['Admin', 'SeazoneAdministrative', 'SeazoneOnboarding'],
      },
      {
        title: 'Perspectiva do Franqueado',
        path: '/painel-perspectiva-do-franqueado',
        permissions: ['Admin', 'SeazoneAdministrative', 'SeazoneOnboarding'],
      },
      {
        title: 'Gestão de Tarefas',
        path: '/gestaodetarefas',
        permissions: ['Admin', 'SeazoneAdministrative', 'SeazoneOnboarding'],
      },
    ],
    Parceiros: [
      {
        title: 'Aprovar Lista',
        path: '/admin/aprovar-lista',
        permissions: ['Admin', 'SeazoneAdministrative'],
      },
      {
        title: 'Editar Parceiro',
        path: '/editardados/parceiro',
        permissions: ['Admin', 'SeazoneOnboarding'],
      },
      {
        title: 'Parceiro',
        path: '/inserirdados/parceiro',
        permissions: ['Admin', 'SeazoneOnboarding'],
      },
      {
        title: 'Gerenciar Executivos',
        path: '/gerenciar-executivos',
        permissions: ['Admin', 'SeazoneOnboarding'],
      },
      {
        title: 'Condomínio B2B',
        path: '/inserirdados/condominio-b2b',
        permissions: ['Admin', 'SeazoneOnboarding'],
      },
      {
        title: 'Perspectiva do Parceiro',
        path: '/painel-perspectiva-do-parceiro',
        permissions: ['Admin', 'SeazoneAdministrative', 'SeazoneOnboarding'],
      },
    ],
    'Sapron Labs': [
      {
        title: 'Acessar Sapron Labs',
        path: '',
        permissions: ['Admin', 'Attendant', 'SeazoneAdministrative', 'SeazoneOnboarding'],
      },
    ],
  };

  const activitiesMappingsHost: UniversesActivitiesOptions = {
    Operacional: [
      {
        title: 'Controle',
        path: '/controle',
        permissions: ['Host'],
      },
      {
        title: 'Multicalendar',
        path: '/multicalendar',
        permissions: ['Host'],
      },
      {
        title: 'Danos de Hóspede',
        path: '/danosdehospede',
        permissions: ['Host'],
      },
      {
        title: 'Pesquisar Propriedade',
        path: '/propriedades',
        permissions: ['Host'],
      },
      {
        title: 'Criação de Subconta',
        path: '/abrir-modal-subconta',
        verifyHostOps: true,
      },
      {
        title: 'Gestão de Tarefas',
        path: '/gestaodetarefas',
        permissions: ['Host'],
      },
    ],
    Financeiro: [
      {
        title: 'Pedido de Reembolso',
        path: '/reembolso',
        permissions: ['Host'],
      },
      {
        title: 'Dashboard',
        path: '/dashboard',
        verifyHostOps: true,
      },
      {
        title: 'Loja Seazone',
        path: 'https://lojaitens.seazone.com.br',
        permissions: ['Host'],
      },
    ],
    Proprietário: [],
    Anfitrião: [
      {
        title: 'Minhas Informações',
        path: '/minhasinformacoes',
        permissions: ['Host'],
      },
    ],
    Parceiros: [
      {
        title: 'Programa de Indicações',
        path: '/parceiros/painel',
        permissions: ['Partner'],
        verifyHostOps: true,
      },
    ],
    'Sapron Labs': [],
  };

  type UserMainRole = string;
  const universeMappings: Record<UserMainRole, UniversesNavItemComponentOptions> = {
    Admin: universeMappingsBackoffice,
    Seazone: universeMappingsBackoffice,
    Attendant: universeMappingsBackoffice,
    Host: universeMappingsHost,
  };

  const activitiesMappings: Record<UserMainRole, UniversesActivitiesOptions> = {
    Admin: activitiesMappingsBackoffice,
    Seazone: activitiesMappingsBackoffice,
    Attendant: activitiesMappingsBackoffice,
    Host: activitiesMappingsHost,
  };

  const universeIconsWhiteMappings: UniversesNavItemComponentOptions = {
    Operacional: <LogoUniverseOperational color="white" />,
    Financeiro: <LogoUniverseFinances color="white" />,
    Proprietário: <LogoUniverseOwner color="white" />,
    Anfitrião: <LogoUniverseHost color="white" />,
    Parceiros: <LogoUniversePartner color="white" />,
    'Sapron Labs': <LogoSapronLabs color="white" />,
  };

  const universeIconsBlueMappings: UniversesNavItemComponentOptions = {
    Operacional: <LogoUniverseOperational color="blue" />,
    Financeiro: <LogoUniverseFinances color="blue" />,
    Proprietário: <LogoUniverseOwner color="blue" />,
    Anfitrião: <LogoUniverseHost color="blue" />,
    Parceiros: <LogoUniversePartner color="blue" />,
    'Sapron Labs': <LogoSapronLabs color="blue" />,
  };

  return {
    universeMappingsBackoffice,
    universeMappingsHost,
    universeMappings: universeMappings[`${userInformation?.main_role}`],
    universeIconsWhiteMappings,
    universeIconsBlueMappings,
    activitiesMappings: activitiesMappings[`${userInformation?.main_role}`],
    activitiesMappingsBackoffice,
    activitiesMappingsHost,
  };
};

export default useUniverseMappings;
